import axios from 'axios'
import { auth } from '../google/auth/firebaseAuth'
import { redirect } from 'react-router'
import { pathEnableSecondFactor, pathSignOut } from '../routes'
import { multiFactor } from 'firebase/auth'
import mystaffTokens from './tokenStore'

class DeferredPromise {
  constructor (endpoint) {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve
      this.reject = reject
      this.endpoint = endpoint || ''
    })
  }
}

export const STATUSTEXTS = {
  MFAREQUIRED: 'User needs mfa',
  REAUTHREQUIRED: 'Credentials too old'
}

class MobileApi {
  #auth = async () => 'Bearer ' + (await auth.currentUser?.getIdToken())
  #currentUser = async () => {
    await auth.authStateReady()
    return auth.currentUser
  }

  #oldAuth = async () => 'Bearer ' + mystaffTokens.getAccess()
  #refreshToken = async () => mystaffTokens.getRefresh()
  #basePath

  constructor () {
    this.#basePath = process.env.PUBLIC_API_URL
  }

  // a function that retries a call, after retrieving new access token.
  async #tryCallAgain (callToTry, params) {
    try {
      let authorization
      if (await this.#currentUser()) {
        await auth.currentUser.getIdTokenResult(true)
        authorization = await auth.currentUser.getIdToken(true)
        params.config.headers.Authorization = 'Bearer ' + authorization
      } else {
        authorization = (await this.getAccessToken())
        mystaffTokens.setRefresh(authorization.data.refresh)
        params.config.headers.Authorization = 'Bearer ' + authorization.data.access
      }
      const responseReTry = await callToTry(...Object.values(params))
      return responseReTry
    } catch (error) {
      // if refresh token did not work, log out user and log unauthorized error
      if (error.response.status === 401) {
        redirect(pathSignOut)
        // page needs to be reloaded so that user sees login screen
        window.location.reload()
      }
      console.error(error)
    }
  }

  #getPath (path) {
    return this.#basePath + path
  }

  async #get (path, params, extraInfo) {
    const config = {
      headers: {
        Authorization: (await this.#currentUser()) ? await this.#auth() : await this.#oldAuth(),
        'X-UI': 'Web'
      },
      ...params
    }
    try {
      const response = await axios.get(this.#getPath(path), config)
      return response
    } catch (error) {
      const response = error.response
      let responseData = response.data
      if (responseData instanceof Blob) {
        responseData = await responseData.text()
      }
      if (response.status === 401) {
        if (response.statusText === STATUSTEXTS.MFAREQUIRED) {
          const mfaUser = multiFactor(await this.#currentUser())
          if (mfaUser.enrolledFactors.length === 0) {
            return redirect(pathEnableSecondFactor, { state: { mfaHideSecondary: true } })
          } else {
            return redirect(pathSignOut)
          }
        } else if (responseData === STATUSTEXTS.REAUTHREQUIRED || response?.statusText === STATUSTEXTS.REAUTHREQUIRED) {
          const deferredPromise = new DeferredPromise(extraInfo?.endpoint)
          // Open re-authentication dialog
          window.dispatchEvent(new window.CustomEvent('reauth', {
            detail: {
              deferredPromise
            }
          }))
          // Wait for reauthentication
          const success = await deferredPromise.promise
          if (success) {
            // TODO: is this needed?
            config.headers.Authorization = await this.#auth()
            // Try the call again
            return await this.#get(path, params)
          } else {
            throw new Error('userNeedsReauth')
          }
          // We need to check the response data here again so that we do not log out the user after we ask them to enable the second factor.
        } else if (responseData !== STATUSTEXTS.MFAREQUIRED) {
          auth.signOut()
          return redirect(pathSignOut)
        }
      } else {
        console.log(error)
        return error
      }
    }
  }

  async #put (path, data, custConf = {}, extraInfo) {
    const config = {
      headers: {
        Authorization: (await this.#currentUser()) ? await this.#auth() : await this.#oldAuth(),
        'X-UI': 'Web'
      },
      ...custConf
    }

    if (custConf.noAuth) {
      delete config.headers.Authorization
    }

    try {
      return await axios.put(this.#getPath(path), data, config)
    } catch (e) {
      let responseData = e.response.data
      if (responseData instanceof Blob) {
        responseData = await responseData.text()
      }
      if (e.response.status === 401 && (await this.#refreshToken())) {
        return await this.#tryCallAgain(axios.put, { url: this.#getPath(path), data, config })
      } else if (responseData === STATUSTEXTS.REAUTHREQUIRED || e?.response?.statusText === STATUSTEXTS.REAUTHREQUIRED) {
        const deferredPromise = new DeferredPromise(extraInfo?.endpoint)
        window.dispatchEvent(new window.CustomEvent('reauth', {
          detail: {
            deferredPromise
          }
        }))
        const success = await deferredPromise.promise
        if (success) {
          config.headers.Authorization = await this.#auth()
          return await this.#put(path, data, custConf)
        } else {
          throw new Error('userNeedsReauth')
        }
      } else {
        console.error(e)
        return e
      }
    }
  }

  async #post (path, data, custConf = {}, extraInfo) {
    const config = {
      headers: {
        Authorization: (await this.#currentUser()) ? await this.#auth() : await this.#oldAuth(),
        'X-UI': 'Web'
      },
      ...custConf
    }

    if (custConf.noAuth) {
      delete config.headers.Authorization
    }

    try {
      return await axios.post(this.#getPath(path), data, config)
    } catch (e) {
      let responseData = e.response.data
      if (responseData instanceof Blob) {
        responseData = await responseData.text()
      }
      if (e.response.status === 401 && (await this.#refreshToken())) {
        return await this.#tryCallAgain(axios.post, { url: this.#getPath(path), data, config })
      } else if (responseData === STATUSTEXTS.REAUTHREQUIRED || e?.response?.statusText === STATUSTEXTS.REAUTHREQUIRED) {
        const deferredPromise = new DeferredPromise(extraInfo?.endpoint)
        window.dispatchEvent(new window.CustomEvent('reauth', {
          detail: {
            deferredPromise
          }
        }))
        const success = await deferredPromise.promise
        if (success) {
          config.headers.Authorization = await this.#auth()
          return await this.#post(path, data, custConf)
        } else {
          throw new Error('userNeedsReauth')
        }
      } else {
        console.error(e)
        return e
      }
    }
  }

  async #delete (path, data, extraInfo) {
    const config = {
      headers: {
        Authorization: (await this.#currentUser()) ? await this.#auth() : await this.#oldAuth(),
        'X-UI': 'Web'
      }
    }
    try {
      return await axios.delete(this.#getPath(path), config)
    } catch (e) {
      if (e.response.status === 401 && (await this.#refreshToken())) {
        return await this.#tryCallAgain(axios.delete, { url: this.#getPath(path), config })
      } else if (e.response.data === STATUSTEXTS.REAUTHREQUIRED || e?.response?.statusText === STATUSTEXTS.REAUTHREQUIRED) {
        const deferredPromise = new DeferredPromise(extraInfo?.endpoint)
        window.dispatchEvent(new window.CustomEvent('reauth', {
          detail: {
            deferredPromise
          }
        }))
        const success = await deferredPromise.promise
        if (success) {
          return await this.#delete(path, data)
        } else {
          throw new Error('userNeedsReauth')
        }
      } else {
        console.error(e)
        return e
      }
    }
  }

  async getUser (auth) {
    const config = {
      headers: {
        Authorization: 'Bearer ' + auth
      }
    }
    const response = await axios.get(this.#basePath + '/v1/User/UserInfo', config)
    return response
  }

  async getCreateShiftData () {
    return await this.#get('/v1/User/CreateShiftData')
  }

  async getOrderShiftData () {
    return await this.#get('/v1/User/CreateCustomerShiftData')
  }

  async getShiftData (shiftId) {
    return await this.#get(`/v1/Shift/Employee/Shift?shiftId=${shiftId}&ui=1`)
  }

  async getCalendarShiftData (shiftId) {
    return await this.#get(`/v1/Shift/Calendar/Shift?shiftId=${shiftId}&ui=1`)
  }

  async getEditShiftData (shiftId) {
    return await this.#get(`/v1/Shift/Employee/Shift/Edit?${shiftId}`)
  }

  async getShiftCodeData (code) {
    return await this.#get(`/v1/Shift/Employee/Shift/Code${code}`)
  }

  async acceptShiftCode (query) {
    return await this.#put(`/v1/Shift/Employee/Shift/Code${query}`)
  }

  async rejectShiftCode (query) {
    return await this.#delete(`/v1/Shift/Employee/Shift/Code${query}`)
  }

  async updateShift (shiftData) {
    return await this.#put('/v1/Shift/Employee/Shift/Update', shiftData)
  }

  async cancelShift (shiftId) {
    return await this.#put(`/v1/Shift/Employee/Shift/Cancel?shiftId=${shiftId}`, '0')
  }

  async saveWorkShifts (shiftData) {
    return await this.#put('/v1/Shift/Employee/Shift/AddMultiple', shiftData)
  }

  async getDoneWorkShifts (query) {
    return await this.#get(`/v1/Shift/Employee/DoneShiftEntriesSortable?${query}`)
  }

  async getPlacesOfWorkForShifts () {
    return await this.#get('/v1/Shift/Employee/DoneShiftEntries/PlacesOfWork')
  }

  async getUpcomingShifts (query) {
    return await this.#get(`/v1/Shift/Employee/UpcomingShifts?${query}`)
  }

  async setLanguage (lang) {
    return await this.#put('/v1/User/Language', { Culture: lang })
  }

  async getUpdateNotices () {
    return await this.#get('/v1/User/UpdateNotices')
  }

  async setNoticeAsRead (id) {
    await this.#put(`/v1/User/UpdateNotices/${id}/MarkAsRead`, '0')
  }

  async getNoticeLink (id) {
    const config = {
      headers: {
        Authorization: await this.#auth()
      },
      responseType: 'blob'
    }
    try {
      return await axios.get(this.#getPath(`/v1/User/UpdateNotices/${id}`), config)
    } catch (error) {
      console.error(error)
      return error
    }
  }

  async getLatestNoticeLink () {
    const config = {
      headers: {
        Authorization: await this.#auth()
      },
      responseType: 'blob'
    }
    try {
      const response = await axios.get(this.#getPath('/v1/User/UpdateNotices/Latest'), config)
      return response
    } catch (error) {
      console.error(error)
      return error
    }
  }

  async getStaffNews () {
    return await this.#get('/v1/User/Staffnews')
  }

  signEmploymentContract = (data) => this.#put('/v1/Employee/SignContract', data, {}, { endpoint: 'signEmploymentContract' })

  async getEmploymentContracts () {
    return await this.#get('/v1/Employee/EmploymentContracts', {}, { endpoint: 'getEmploymentContracts' })
  }

  async getEmploymentContractPdf (index) {
    try {
      return await this.#get(`/v1/Employee/EmploymentContractPDF?index=${index}`, { responseType: 'blob' }, { endpoint: 'getEmploymentContractPdf' })
    } catch (e) {
      return e
    }
  }

  async getEmploymentContractPreview (contractId) {
    try {
      return await this.#get(`/v1/Employee/EmploymentContractPreview?contractId=${contractId}`, {}, { endpoint: 'getEmploymentContractPreview' })
    } catch (e) {
      return e
    }
  }

  async getEmploymentAttachmentPreview (attachmentId) {
    try {
      return await this.#get(`/v1/Employee/EmploymentContractAttachmentPreview?attachmentId=${attachmentId}`, {}, { endpoint: 'getEmploymentAttachmentPreview' })
    } catch (e) {
      return e
    }
  }

  async getEmploymentContractPreviewPDF (contractId) {
    try {
      const config = {
        headers: {
          Authorization: await this.#auth()
        },
        responseType: 'blob'
      }
      return await this.#get(`/v1/Employee/EmploymentContractPreviewPDF?contractId=${contractId}`, config, { endpoint: 'getEmploymentContractPreviewPDF' })
    } catch (e) {
      return e
    }
  }

  async getEmploymentAttachmentPreviewPDF (contractId) {
    try {
      const config = {
        headers: {
          Authorization: await this.#auth()
        },
        responseType: 'blob'
      }
      return await this.#get(`/v1/Employee/EmploymentContractAttachmentPreviewPDF?attachmentId=${contractId}`, config, { endpoint: 'getEmploymentAttachmentPreviewPDF' })
    } catch (e) {
      return e
    }
  }

  /**
   * @param {Object} data Optional parameter defining what message to fetch
   * @param {Number} data.skip How many entries will be skipped from the start of result (default is 0)
   * @param {Number} data.take How many entries will be included in the result (default is 10)
   * @param {Date} data.from Show only messages that is sent from or after given date and time
   * @param {Date} data.until Show only messages that take place before or until given date and time
   * @param {String} data.messageForm Show only certain type of messages (default is (PushNotification))
   * @param {Boolean} data.onlyUnopened Get only messages which are not marked opened (Push-notifications can be marked as opened)
   * @param {Number} data.messageType Show only messages of a certain type (shift suggestion or other)
   * @returns {Promise&Array<
   * {Id: Number,
   * Subject: String,
   * Message: String,
   * SendTime: Date,
   * MessageTypeId: Number,
   * Sender: String,
   * MessageFormId: Number,
   * SendTo: String,
   * ShiftId: Number,
   * IsOpened: Boolean,
   * MessageToken: String}>} Message log Promise -> Array
   */
  async getMessages (data = { skip: null, take: null, from: null, until: null, messageForm: null, onlyUnopened: null, messageType: null }) {
    let url = '/v1/Message/Employee/Messages?null'
    Object.keys(data).forEach(key => {
      url += `&${key}=${data[key]}`
    })
    return await this.#get(url)
  }

  // async downloadPdf (path) {
  //   const config = {
  //     headers: {
  //       Authorization: await this.#auth()
  //     },
  //     responseType: 'blob'
  //   }
  //   return await axios.get(path, config)
  // }

  async getUserInfo () {
    return await this.#get('/v1/User/UserInfo')
  }

  async getNextOfKin () {
    return await this.#get('/v1/User/NOK', {}, { endpoint: 'NOK' })
  }

  async getHolidayManagementLink (auth) {
    return await this.#get('/v1/Employee/GetHolidayManagementLink', undefined, auth || (await this.#auth()))
  }

  async getEmployeePaymentSchedules () {
    return await this.#get('/v1/Salary/Employee/SalaryPaymentSchedules', {}, { endpoint: 'SalaryPaymentSchedules' })
  }

  /**
   *
   * @returns {Array<{
   * PaymentDate (string),
   * VersionNumber (integer),
   * DownloadToken (string)>} Returns an array of salary slip models with download tokens for employee
   */
  async getSalarySlips () {
    return await this.#get('/v1/Salary/Salaryslips', {}, { endpoint: 'SalarySlips' })
  }

  /**
   * Fetches a single salary slip as a blob that can be made into a pdf
   * @param {Object} token What salary slip to fetch
   * @returns A single salary slip as blob data
   */
  async getSalarySlipsPdf (token) {
    const headers = {
      headers: {
        Client: 'myStaffMobileApp',
        DownloadKey: token,
        Authorization: await this.#auth()
      },
      responseType: 'blob'
    }
    return await this.#put('/v1/Salary/Salaryslips/slip', '0', headers, { endpoint: 'SalarySlipPdf' })
  }

  async getAnnualLeaveBalance () {
    return await this.#get('/v1/Employee/AnnualLeaveBalance')
  }

  async getPushNotificationSettings () {
    return await this.#get('/v1/PushNotification/Settings')
  }

  async getIdentityCard () {
    return await this.#get('/v1/Employee/IdentityCard')
  }

  async getIdentityCardImage () {
    return await this.#get('/v1/Employee/IdentitycardImage', { responseType: 'blob' })
  }

  async getNotificationOptions () {
    return await this.#get('/v1/PushNotification/NotificationOptions')
  }

  async getActiveContract () {
    return await this.#get('/v1/Employee/ActiveEmploymentContract')
  }

  async createEmploymentCertificate (temporary) {
    return await this.#get(`/v1/Reporting/CertificationOfEmploymentReport?doNotPrint=true&temporaryCertificate=${temporary}`)
  }

  async setWorkSuggestion (data) {
    return await this.#put(`/v1/PushNotification/WorkSuggestions?preferredNotification=${data}`, '0', {}, { endpoint: 'saveWorkSuggestion' })
  }

  async savePersonalAddress (addr) {
    return await this.#put('/v1/User/Employee/Address', addr, {}, { endpoint: 'savePersonalAddress' })
  }

  async saveBank (data) {
    return await this.#put('/v1/User/Employee/Bank', data, {}, { endpoint: 'saveBank' })
  }

  async saveNextOfKin (data) {
    return await this.#put('/v1/User/Employee/NextOfKin', data, {}, { endpoint: 'saveNextOfKin' })
  }

  async savePersonalInfo (data) {
    return await this.#put('/v1/User/Employee/PersonalInfo', data, {}, { endpoint: 'savePersonalInfo' })
  }

  async changePassword (oldPassword, newPassword) {
    const passwordModel = {
      OldPassword: oldPassword,
      NewPassword: newPassword
    }
    return await this.#put('/v1/User/Password', passwordModel, {}, { endpoint: 'changePassword' })
  }

  async changeEmail (data) {
    return await this.#put(`/v1/User/UpdateEmail?email=${data}`, '0', {}, { endpoint: 'changeEmail' })
  }

  // async getFaqTexts (int) {
  //   return await this.#get('/v1/User/ContactText', int)
  // }

  /**
   * Sends an email from user to the sendTo address.
   * Emails will NOT be sent when testing locally.
   * In test environment emails are sent to specified addresses only (check Saima.Common configs)
   * @param {string} subject
   * @param {string} message
   * @param {string} sendTo
   */
  async sendEmailFromUser (subject, message, sendTo) {
    const MessageModel = {
      Subject: subject,
      Message: message,
      SendTo: sendTo
    }

    return await this.#put('/v1/Support/SendEmail', MessageModel)
  }

  // UNTESTED. WAITING FOR DATABASE FUNCTIONALITY
  async activateForNotifications (auth, token) {
    this.#put('/v1/PushNotification/Activate', token)
  }

  async deactivateForNotifications (auth, token) {
    this.#put('/v1/PushNotification/Deactivate', token)
  }

  async getOpenVacancies (query) {
    return await this.#get(`/v1/Employee/SearchVacancies?${query}`)
  }

  async likeVacancy (vacancyId) {
    return await this.#put(`/v1/Employee/LikeVacancy?vacancyId=${vacancyId}&like=true`, '0')
  }

  async dislikeVacancy (vacancyId) {
    return await this.#put(`/v1/Employee/LikeVacancy?vacancyId=${vacancyId}&like=false`, '0')
  }

  async getVacancyJobFields () {
    return await this.#get('/v1/Employee/GetVacancyJobFields')
  }

  async getMaintenancePreInfoMessage () {
    return await axios.get(this.#getPath('/v1/System/MaintenancePreInfoMessage'))
  }

  async getCalendarEvents (year, month) {
    return await this.#get(`/v1/Calendar/${year}/${month}`)
  }

  async getPoolConnections () {
    return await this.#get('/v1/Pool/Employee/PoolConnections')
  }

  async sendCreateAccountLink (email) {
    return await this.#post('/v1/User/AccountCreationLink', { Email: email }, { noAuth: true }, { endpoint: 'sendCreateAccountLink' })
  }

  async linkAccountWithToken (token) {
    return await this.#post('/v1/User/LinkFirebaseAccount', token, { noAuth: true }, { endpoint: 'linkAccountWithToken' })
  }

  async acceptPoolInvitation (id) {
    return await this.#put(`/v1/Pool/Employee/Invitation/Accept/${id}`, '0')
  }

  async rejectPoolInvitation (id) {
    return await this.#put(`/v1/Pool/Employee/Invitation/Reject/${id}`, '0')
  }

  async removePool (id) {
    return await this.#delete(`/v1/Pool/Employee/PoolConnections/${id}`)
  }

  // call uses axios straight to prevent infinite loop
  async getAccessToken (auth) {
    const params =
    {
      fromApp: 'newStaff'
    }
    const config = {
      auth: await auth,
      params
    }
    return await axios.post(this.#getPath('/v1/User/AccessToken'), {}, config)
  }

  async listUsers (skip, take, customers, search, auth) {
    const config = {
      headers: {
        Authorization: 'Bearer ' + auth
      },
      skip,
      take,
      customers,
      search
    }
    let url = `/v1/User/ListUsers?skip=${skip}&take=${take}`
    if (search !== null) url += `&search=${search}`
    if (customers !== null) url += `&customers=${customers}`
    return await this.#get(url, config)
  }

  async mintFirebaseToken (firebaseUID, auth) {
    const config = {
      headers: {
        Authorization: 'Bearer ' + auth
      },
      params: {
        firebaseUID
      }
    }
    return await this.#post('/v1/User/MintFirebaseToken/', { firebaseUID }, config)
  }

  async mintStaffpointToken (username, auth) {
    const config = {
      headers: {
        Authorization: 'Bearer ' + auth
      },
      params: {
        username,
        fromApp: 'newStaff'
      }
    }
    return await this.#post('/v1/User/MintStaffpointToken/', { username, fromApp: 'newStaff' }, config)
  }

  async updateEmailAddress (email) {
    return await this.#put(`/v1/User/UpdateEmail?email=${email}`, { email })
  }

  async saveCalendarNotAvailable (eventBundle) {
    return await this.#put('/v1/Calendar/Employee/Events/Add/NotAvailable', eventBundle)
  }

  async saveCalendarShiftRequest (eventBundle) {
    return await this.#put('/v1/Calendar/Employee/Events/Add/ShiftRequest', eventBundle)
  }

  async updateCalendarEvents (eventBundle, eventId) {
    return await this.#put(`/v1/Calendar/Employee/Events/Edit/${eventId}`, eventBundle)
  }

  async deleteCalendarNotAvailable (id) {
    return await this.#delete(`/v1/Calendar/Employee/Events/NotAvailable/${id}`)
  }

  async deleteCalendarShiftRequest (id) {
    return await this.#delete(`/v1/Calendar/Employee/Events/ShiftRequest/${id}`)
  }

  async markShiftInfoAsRead (params) {
    return await this.#put(`/v1/Shift/Employee/Shift/MarkInfoAsRead?${params}`, '0')
  }

  async acceptShiftTimeChanges (params) {
    return await this.#put(`/v1/Shift/Employee/Shift/AcceptShiftTimeChanges?${params}`, '0')
  }

  async rejectShiftTimeChanges (params) {
    return await this.#put(`/v1/Shift/Employee/Shift/RejectShiftTimeChanges?${params}`, '0')
  }

  async acceptSuggestedShift (id) {
    return await this.#put(`/v1/Shift/Employee/SuggestedShifts/Book/${id}`, '0')
  }

  async rejectSuggestedShift (id) {
    return await this.#delete(`/v1/Shift/Employee/SuggestedShifts/${id}`)
  }

  async getOpenAndSuggestedShifts (params) {
    return await this.#get(`/v1/Shift/Employee/SuggestedOrOpenShifts?${params}`)
  }

  async rejectSuggestedBundle (id) {
    return await this.#delete(`/v1/Shift/Employee/SuggestedBundleShift/${id}`)
  }

  async acceptSuggestedBundle (id) {
    return await this.#put(`/v1/Shift/Employee/BundledShifts/Accept/${id}`, '0')
  }

  async getTopAlerts () {
    return await this.#get('/v1/Employee/GetTopAlerts')
  }

  async getSharedFiles () {
    return await this.#get('/v1/Employee/SharedFiles', {}, { endpoint: 'SharedFiles' })
  }

  async getSingleFile (id) {
    try {
      return await this.#get(`/v1/Employee/SharedFiles/${id}`, { responseType: 'blob' }, { endpoint: 'getSharedFile' })
    } catch (e) {
      return e
    }
  }

  async setEmailDisabled (disable) {
    return await this.#put('/v1/Employee/SetDisableEmail', '0', { params: { disable } }, { endpoint: 'setEmailDisabled' })
  }

  async getEmailSettings () {
    return await this.#get('/v1/Employee/EmailSettings')
  }

  async getWorkTimePeriods () {
    return await this.#get('/v1/Employee/WorkTimePeriods', {}, { endpoint: 'WorkTimePeriods' })
  }

  async getContractsOrPayslips () {
    return await this.#get('/v1/Employee/ContractsOrPayslips')
  }

  async sendVerificationLink (email) {
    return await this.#put(`/v1/User/SendLinkToEmail/${email}`, '0', { noAuth: true })
  }

  async getEmployeeTrainingMaterials () {
    return await this.#get('/v1/Employee/EmployeeTrainingMaterials')
  }

  async getSingleEmployeeTrainingMaterial (fileId) {
    try {
      return await this.#get(`/v1/Employee/EmployeeTrainingMaterials/${fileId}`, { responseType: 'blob' })
    } catch (e) {
      return e
    }
  }

  async markEmployeeTrainingMaterialAsRead (fileId) {
    return await this.#put(`/v1/Employee/MarkEmployeeTrainingMaterialAsRead/${fileId}`, '0')
  }

  async resetPassword (identifier) {
    const params = new URLSearchParams({ identifier }).toString()
    return await this.#put(`/v1/User/ResetPassword?${params}`, '0', { noAuth: true })
  }

  // Customer
  async getCustomerPrint (query) {
    return await this.#get(`/v1/Prints/Customer/PrintReport?${query}`, { responseType: 'blob' })
  }

  async getCustomerTopAlerts () {
    return await this.#get('/v1/PlaceOfWork/CustomerTopAlerts')
  }

  async getCustomerPlaceOfWork () {
    return await this.#get('/v1/Prints/Customer/PlacesOfWork')
  }

  async acceptCustomerShifts (arrayOfIds) {
    return await this.#put('/v1/Shift/Customer/Accept', arrayOfIds)
  }

  async cancelCustomerShifts (cancelModel) {
    return await this.#put('/v1/Shift/Customer/Cancel', cancelModel)
  }

  async reportShift (data) {
    return await this.#put('/v1/Shift/Customer/CreateEntries', data)
  }

  async getEditCustomerShift (shiftIdQuery) {
    return await this.#get(`/v1/Shift/Customer/Shift/Edit?${shiftIdQuery}`)
  }

  async updateCustomerShift (shiftData) {
    return await this.#put('/v1/Shift/Customer/Shift/Update', shiftData)
  }

  async checkAvailableEmployeesToSuggest (params) {
    return await this.#get(`/v1/Shift/Customer/Shift/CheckPoolEmployeesToSuggest?${params}`)
  }

  async getAvailableEmployeesForShifts (params) {
    return await this.#get(`/v1/Shift/Customer/Shift/EmployeesToSuggest?${params}`)
  }

  // Deprecated, use createShiftMultiple instead
  async createShift (data) {
    return await this.#put('/v1/Shift/Customer/Create', data)
  }

  async createShiftMultiple (data) {
    return await this.#put('/v1/Shift/Customer/CreateMultiple', data)
  }

  async getCustomerPlaceOfWorkList () {
    return await this.#get('/v1/PlaceOfWork/CustomerUserPlaceOfWorkList')
  }

  async getCustomerPlaceOfWorkListV2 ({ sortBy, skip, take, searchString } = {}) {
    const params = new URLSearchParams()
    if (sortBy) params.set('sortBy', sortBy)
    if (skip != null) params.set('skip', skip.toString())
    if (take != null) params.set('take', take.toString())
    if (searchString) params.set('searchString', searchString)
    return await this.#get(`/v1/PlaceOfWork/CustomerUserPlaceOfWorkListV2?${params}`)
  }

  async getCustomerPlaceOfWorkData (placeOfWorkId) {
    return await this.#get(`/v1/PlaceOfWork/CustomerPlaceOfWorkData?placeOfWorkId=${placeOfWorkId}`)
  }

  async saveMyOfficeEdit (model) {
    return await this.#put('/v1/PlaceOfWork/MyOfficeEdit', model)
  }

  async getCountries () {
    return await this.#get('/v1/PlaceOfWork/Countries')
  }

  async getProfessionTitles () {
    return await this.#get('/v1/Shift/Customer/ProfessionTitles')
  }

  async getCustomerAttributes () {
    return await this.#get('/v1/Shift/Customer/Attributes')
  }

  async getSuggestedEmployees (shiftIdQuery) {
    return await this.#get(`/v1/Shift/Customer/Shift/SuggestedEmployees?${shiftIdQuery}`)
  }

  async getAllCustomerShifts (query) {
    return await this.#get(`/v1/Shift/Customer/AllShifts?${query}`)
  }

  async getCustomerShiftSummary (query) {
    return await this.#get(`/v1/Shift/Customer/ShiftSummary?${query}`)
  }

  async getCustomerUnacceptedShiftsCount () {
    return await this.#get('/v1/Shift/Customer/UnapprovedShiftCount')
  }

  async acceptAllCustomerShifts () {
    return await this.#put('/v1/Shift/Customer/AcceptAllUnAcceptedShifts')
  }

  async customerUpdateFeedback (feedback) {
    return await this.#put('/v1/Shift/Customer/Shift/Feedback', feedback)
  }

  async customerAddToPool (data) {
    return await this.#put('/v1/Pool/Customer/Employees/Add', data)
  }

  async getCustomerPoolEmployeesAndSuggestions (query) {
    return await this.#get(`/v1/Pool/Customer/EmployeesAndSuggestions?${query}`)
  }

  async getPoolProfessionTitles () {
    return await this.#get('/v1/Pool/Customer/ProfessionTitles')
  }

  async getCustomerPoolPlacesOfWork () {
    return await this.#get('/v1/Pool/Customer/PlaceOfWorkValues')
  }

  async removeCustomerPoolEmployee (poolConnectionId) {
    return await this.#delete(`/v1/Pool/Customer/Employees/${poolConnectionId}`)
  }

  async customerSendSms (data) {
    return await this.#put('/v1/Message/Customer/SendSms', data)
  }

  async getCustomerOrganizations () {
    return await this.#get('/v1/TSP/CustomerOrganizations')
  }

  async getCustomerInternalEmployeePlacesOfWork (fromData) {
    return await this.#post('/v1/TSP/SelectablePlacesOfBusiness', fromData)
  }

  async createInternalEmployee (formData) {
    return await this.#post('/v1/TSP/CreateInternalEmployee', formData)
  }

  async getCustomerInternalEmployees (query) {
    return await this.#get(`/v1/TSP/CustomerInternalResourcesList?${query}`)
  }

  async getCustomerInternalEmployeeById (internalEmployeeId, initiallySelectedEmploymentRelationshipId) {
    return await this.#get(`/v1/TSP/InternalEmployeeEditView?internalEmployeeId=${internalEmployeeId}&initiallySelectedEmploymentRelationshipId=${initiallySelectedEmploymentRelationshipId}`)
  }

  async getCustomerRelationships () {
    return await this.#get('/v1/TSP/CustomerRelationships')
  }

  async getEmployeePlaceOfBusiness (internalEmployeeId) {
    return await this.#get(`/v1/TSP/EmployeePlaceOfBusiness?employeeId=${internalEmployeeId}`)
  }

  async saveEmployeePlaceOfBusiness (model) {
    return await this.#put('/v1/TSP/EmployeePlaceOfBusinessSave', model)
  }

  async saveInternalEmployee (model) {
    return await this.#put('/v1/TSP/InternalEmployeeEditSave', model)
  }

  async getCustomerContactPersonInfo () {
    return await this.#get('/v1/MyInfo/ContactPersonEditView')
  }

  async updateCustomerContactPersonInfo (model) {
    return await this.#put('/v1/MyInfo/ContactPersonUpdate', model)
  }

  async getCustomerOpeningHours () {
    return await this.#get('/v1/ContactUs/CustomerOpeningHours')
  }

  async getContactUsView () {
    return await this.#get('/v1/ContactUs/ContactUs')
  }

  async getRecruitOrderView () {
    return await this.#get('/v1/RecruitOrder/RecruitOrderView')
  }

  async getCustomerProfessionTitles (customerId) {
    return await this.#get(`/v1/RecruitOrder/CustomerProfessionTitleOptions?customerId=${customerId}`)
  }

  async sendRecruitOrder (model) {
    return await this.#put('/v1/RecruitOrder/SendRecruitOrder', model)
  }
  // -Customer
}

const mobileApi = new MobileApi()
export default mobileApi
